/**
 * SDTestApp bench APIs — Main, Batch Tests, Settings, Firmware, Command Docs.
 */

import { apiFetch } from "@/lib/api";
import { buildSdTestAppFetchBaseUrl, SDTESTAPP_DEFAULT_PORT } from "./sdTestAppHttpClient";
import type { ProductionBatchStatus, ProductionSlotsState } from "./sdTestAppProductionClient";

export interface BenchSequenceRow {
  id: string;
  name: string;
  setName: string;
  stepCount?: number;
  source?: string;
}

export interface BenchPowerStatus {
  calibrationRunning: boolean;
  calibrationStatusText: string;
  calibrationActionState: string;
  canUsePowerControls: boolean;
  powerRunning: boolean;
  appCalibrationStatusText: string;
}

export interface BenchAppCalibrationPoint {
  pointIndex: number;
  outputPercent: number | null;
  measuredAmps: number | null;
  targetAmps?: number | null;
}

export interface FineTuneCalibrationSnapshot {
  open: boolean;
  currentPoint: number;
  retryMode: boolean;
  description: string;
  storedMeasuredAmps: number | null;
  storedOutputPercent: number | null;
  defaultPlaySeconds: number;
  canOpen: boolean;
  gateReason?: string | null;
  calibrationTargets?: number[];
}

export interface BenchSettingsField {
  name: string;
  label: string;
  section: string;
  type: string;
  requiresRestart: boolean;
  value: unknown;
}

export interface BenchSettingsAbout {
  applicationName: string;
  version: string;
  releasesUrl: string;
}

export interface BenchFirmwareStepResult {
  stepName: string;
  passed: boolean;
  label: "PASS" | "FAIL";
  message: string;
  detail: string;
}

export interface BenchFirmwareSuiteResult {
  suiteId: string;
  suiteName: string;
  passed: boolean;
  overallLabel: "PASS" | "FAIL";
  firmwareVersion?: string;
  connectorUniqueId?: string;
  errorMessage?: string;
  steps: BenchFirmwareStepResult[];
}

export interface BenchConfigureAllResult {
  success: boolean;
  modelNumber: string;
  targetCount: number;
  successCount: number;
  error?: string | null;
  results: Array<{ connectorId: string; success: boolean; error?: string | null }>;
}

export interface BenchFirmwareSuite {
  suiteId: string;
  name: string;
  description: string;
  minimumFirmware?: string | null;
  ticketReference?: string;
}

export interface BenchCommandRegistryEntry {
  commandKey: string;
  label: string;
  protocol?: string | null;
  index?: number;
}

export interface BenchStepTreeEntry {
  type: string;
  label: string;
  nestedSequenceOrigin?: string | null;
}

export interface BenchLiveStepTreeState {
  live: boolean;
  entries: BenchStepTreeEntry[];
  currentIndex?: number | null;
  completedIndices?: number[];
  skipIndices?: number[];
  version?: number;
}

export interface BenchDocumentationRule {
  appliesTo: string;
  pattern: string;
  fromExample: string;
  toExample: string;
  explanation: string;
  importantNotes?: string[];
}

export interface BenchDocumentationPayload {
  intro: string;
  rules: BenchDocumentationRule[];
}

export interface BenchCommandResult {
  success: boolean;
  message?: string;
  payload?: unknown;
  executionTrace?: unknown;
  connectorId?: string;
  queued?: boolean;
}

function normalizeError(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error);
}

export class SdTestAppBenchClient {
  private readonly baseUrl: string;

  constructor(port: string = SDTESTAPP_DEFAULT_PORT) {
    this.baseUrl = buildSdTestAppFetchBaseUrl(port);
  }

  private async json<T>(path: string, init?: RequestInit): Promise<T> {
    let response: Response;
    try {
      response = await fetch(`${this.baseUrl}${path}`, init);
    } catch (error) {
      throw new Error(normalizeError(error));
    }
    const payload = (await response.json()) as T & { error?: string };
    if (!response.ok) {
      throw new Error(String(payload.error ?? `Request failed (${response.status})`));
    }
    return payload;
  }

  async fetchBatchSequences(): Promise<BenchSequenceRow[]> {
    const payload = await this.json<{ sequences: BenchSequenceRow[] }>("/api/hub/v1/batch/sequences");
    return payload.sequences ?? [];
  }

  async fetchBatchStatus(): Promise<ProductionBatchStatus> {
    return this.json<ProductionBatchStatus>("/api/hub/v1/batch/status");
  }

  async fetchBatchStepTree(sequenceId: string, sequenceSet?: string): Promise<BenchStepTreeEntry[]> {
    const params = new URLSearchParams({ sequenceId });
    if (sequenceSet) params.set("sequenceSet", sequenceSet);
    const payload = await this.json<{ entries?: BenchStepTreeEntry[] }>(
      `/api/hub/v1/batch/step-tree?${params.toString()}`,
    );
    return payload.entries ?? [];
  }

  async fetchLiveBatchStepTree(): Promise<BenchLiveStepTreeState> {
    return this.json<BenchLiveStepTreeState>("/api/hub/v1/batch/step-tree/live");
  }

  async fetchDocumentation(): Promise<BenchDocumentationPayload> {
    return this.json<BenchDocumentationPayload>("/api/hub/v1/documentation");
  }

  /** Loopback-only helper for remote observe via Hub relay. */
  static async relayJson<T>(machineId: string, hubApiPath: string): Promise<T> {
    const normalizedPath = hubApiPath.replace(/^\/+/, "");
    return apiFetch<T>(`/api/benches/${encodeURIComponent(machineId)}/relay/${normalizedPath}`);
  }

  async startBatch(input: {
    sequenceId: string;
    sequenceSet?: string;
    slots?: string[];
    manualSequence?: unknown;
    operationType?: string;
  }): Promise<void> {
    await this.json("/api/hub/v1/batch/start", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
  }

  async stopBatch(): Promise<void> {
    await this.json("/api/hub/v1/batch/stop", { method: "POST" });
  }

  async resyncSequences(): Promise<{ success: boolean; skuTemplateCount?: number }> {
    return this.json("/api/hub/v1/batch/resync", { method: "POST" });
  }

  async configureAllNonReference(modelNumber: string): Promise<BenchConfigureAllResult> {
    return this.json<BenchConfigureAllResult>("/api/hub/v1/batch/configure-all", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ modelNumber }),
    });
  }

  async createCustomSequence(input: {
    sequenceName: string;
    manualSequence: string;
    description?: string;
    setName?: string;
  }): Promise<{ success: boolean }> {
    return this.json("/api/hub/v1/batch/custom-sequences", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
  }

  async deleteCustomSequence(sequenceName: string, setName = "Custom Set"): Promise<{ success: boolean }> {
    return this.json("/api/hub/v1/batch/custom-sequences", {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sequenceName, setName }),
    });
  }

  async configureDeviceSku(connectorId: string, modelNumber: string): Promise<{ success: boolean; error?: string | null }> {
    return this.json(`/api/hub/v1/devices/${encodeURIComponent(connectorId)}/configure-sku`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ modelNumber }),
    });
  }

  async fetchConsoleLog(connectorId: string): Promise<string[]> {
    const payload = await this.json<{ lines?: string[] }>(
      `/api/hub/v1/devices/${encodeURIComponent(connectorId)}/console-log`,
    );
    return payload.lines ?? [];
  }

  static relayConsoleLog(machineId: string, connectorId: string): Promise<string[]> {
    return SdTestAppBenchClient.relayJson<{ lines?: string[] }>(
      machineId,
      `/api/hub/v1/devices/${encodeURIComponent(connectorId)}/console-log`,
    ).then((payload) => payload.lines ?? []);
  }

  static relaySettings(machineId: string): Promise<BenchSettingsField[]> {
    return SdTestAppBenchClient.relayJson<{ fields?: BenchSettingsField[] }>(machineId, "/api/hub/v1/settings").then(
      (payload) => payload.fields ?? [],
    );
  }

  static relaySettingsAbout(machineId: string): Promise<BenchSettingsAbout> {
    return SdTestAppBenchClient.relayJson<BenchSettingsAbout>(machineId, "/api/hub/v1/settings/about");
  }

  static relayFirmwareSuites(machineId: string): Promise<BenchFirmwareSuite[]> {
    return SdTestAppBenchClient.relayJson<{ suites?: BenchFirmwareSuite[] }>(
      machineId,
      "/api/hub/v1/firmware-testing/suites",
    ).then((payload) => payload.suites ?? []);
  }

  static relayCommandRegistry(machineId: string): Promise<BenchCommandRegistryEntry[]> {
    return SdTestAppBenchClient.relayJson<{ commands?: BenchCommandRegistryEntry[] }>(
      machineId,
      "/api/hub/v1/command-docs/registry",
    ).then((payload) => payload.commands ?? []);
  }

  async fetchSlots(): Promise<ProductionSlotsState> {
    return this.json<ProductionSlotsState>("/api/hub/v1/production/slots");
  }

  async assignSlot(slotName: string, connectorId: string | null): Promise<ProductionSlotsState> {
    if (!connectorId) {
      return this.json<ProductionSlotsState>(`/api/hub/v1/production/slots/${encodeURIComponent(slotName)}`, {
        method: "DELETE",
      });
    }
    return this.json<ProductionSlotsState>(`/api/hub/v1/production/slots/${encodeURIComponent(slotName)}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ connectorId }),
    });
  }

  async syncSlots(): Promise<ProductionSlotsState> {
    return this.json<ProductionSlotsState>("/api/hub/v1/production/slots/sync", { method: "POST" });
  }

  async setReferenceDevice(connectorId: string): Promise<void> {
    await this.json("/api/hub/v1/production/reference-device", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ connectorId }),
    });
  }

  async fetchPowerStatus(): Promise<BenchPowerStatus> {
    return this.json<BenchPowerStatus>("/api/hub/v1/power/status");
  }

  async startCalibration(): Promise<void> {
    await this.json("/api/hub/v1/power/calibration/start", { method: "POST" });
  }

  async cancelCalibration(): Promise<void> {
    await this.json("/api/hub/v1/power/calibration/cancel", { method: "POST" });
  }

  async startPower(percent: number): Promise<void> {
    await this.json("/api/hub/v1/power/start", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ percent }),
    });
  }

  async stopPower(): Promise<void> {
    await this.json("/api/hub/v1/power/stop", { method: "POST" });
  }

  async fetchAppCalibrationPoints(): Promise<BenchAppCalibrationPoint[]> {
    const payload = await this.json<{ points?: BenchAppCalibrationPoint[] }>("/api/hub/v1/power/app-calibration/points");
    return payload.points ?? [];
  }

  async saveAppCalibrationPoint(input: {
    pointIndex: number;
    outputPercent: number;
    measuredAmps: number;
  }): Promise<void> {
    await this.json("/api/hub/v1/power/app-calibration/points", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
  }

  async fetchFineTuneSnapshot(): Promise<FineTuneCalibrationSnapshot> {
    return this.json<FineTuneCalibrationSnapshot>("/api/hub/v1/power/fine-tune");
  }

  async openFineTuneCalibration(): Promise<FineTuneCalibrationSnapshot> {
    return this.json<FineTuneCalibrationSnapshot>("/api/hub/v1/power/fine-tune/open", { method: "POST" });
  }

  async previewFineTuneCalibration(input: { pointIndex: number; playSeconds?: number }): Promise<{ success: boolean }> {
    return this.json("/api/hub/v1/power/fine-tune/preview", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
  }

  async confirmFineTuneCalibration(input: {
    measuredAmps: number;
    playSeconds?: number;
  }): Promise<FineTuneCalibrationSnapshot & { success: boolean; complete?: boolean; message?: string }> {
    return this.json("/api/hub/v1/power/fine-tune/confirm", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
  }

  async stopFineTuneCalibration(): Promise<FineTuneCalibrationSnapshot> {
    return this.json<FineTuneCalibrationSnapshot>("/api/hub/v1/power/fine-tune/stop", { method: "POST" });
  }

  async exportAppCalibrationCsv(): Promise<{ csvText: string; filename: string }> {
    return this.json<{ csvText: string; filename: string }>("/api/hub/v1/power/app-calibration/export-csv");
  }

  async importAppCalibrationCsv(csvText: string): Promise<{ success: boolean; error?: string }> {
    return this.json("/api/hub/v1/power/app-calibration/import-csv", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ csvText }),
    });
  }

  async startManualVolume(percent: number, maxSeconds = 30): Promise<{ success: boolean; error?: string }> {
    return this.json("/api/hub/v1/power/manual-volume", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ percent, maxSeconds }),
    });
  }

  async startPowerSequence(sequence: string): Promise<{ success: boolean; error?: string; stepCount?: number }> {
    return this.json("/api/hub/v1/power/sequence/start", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sequence }),
    });
  }

  async sendConsole(connectorId: string, input: { mode: "ascii" | "m2m"; text?: string; hex?: string }): Promise<void> {
    await this.json(`/api/hub/v1/devices/${encodeURIComponent(connectorId)}/console`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
  }

  async runDeviceCommand(
    connectorId: string,
    input: { commandKey: string; args?: Record<string, unknown>; wait?: boolean },
  ): Promise<BenchCommandResult> {
    return this.json<BenchCommandResult>(`/api/hub/v1/devices/${encodeURIComponent(connectorId)}/commands`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
  }

  async queryBattery(connectorId: string): Promise<BenchCommandResult> {
    return this.json<BenchCommandResult>(`/api/hub/v1/devices/${encodeURIComponent(connectorId)}/battery-query`, {
      method: "POST",
    });
  }

  async setFastMode(connectorId: string, enabled: boolean): Promise<BenchCommandResult> {
    return this.json<BenchCommandResult>(`/api/hub/v1/devices/${encodeURIComponent(connectorId)}/fast-mode`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ enabled }),
    });
  }

  async fetchSettings(): Promise<BenchSettingsField[]> {
    const payload = await this.json<{ fields: BenchSettingsField[] }>("/api/hub/v1/settings");
    return payload.fields ?? [];
  }

  async fetchSettingsAbout(): Promise<BenchSettingsAbout> {
    return this.json<BenchSettingsAbout>("/api/hub/v1/settings/about");
  }

  async exitApplication(): Promise<void> {
    await this.json("/api/hub/v1/settings/exit", { method: "POST" });
  }

  async checkForUpdates(): Promise<{ success: boolean; status: string; message?: string }> {
    return this.json<{ success: boolean; status: string; message?: string }>(
      "/api/hub/v1/settings/check-for-updates",
      { method: "POST" },
    );
  }

  async saveSetting(fieldName: string, value: unknown): Promise<void> {
    await this.json(`/api/hub/v1/settings/${encodeURIComponent(fieldName)}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ value }),
    });
  }

  async fetchFirmwareSuites(): Promise<BenchFirmwareSuite[]> {
    const payload = await this.json<{ suites: BenchFirmwareSuite[] }>("/api/hub/v1/firmware-testing/suites");
    return payload.suites ?? [];
  }

  async runFirmwareSuite(
    suiteId: string,
    connectorId: string,
  ): Promise<{ connectorId: string; result: BenchFirmwareSuiteResult }> {
    return this.json<{ connectorId: string; result: BenchFirmwareSuiteResult }>(
      `/api/hub/v1/firmware-testing/suites/${encodeURIComponent(suiteId)}/run`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ connectorId }),
      },
    );
  }

  async runAllFirmwareSuites(
    connectorId: string,
  ): Promise<{ connectorId: string; passedCount: number; totalCount: number; results: BenchFirmwareSuiteResult[] }> {
    return this.json<{
      connectorId: string;
      passedCount: number;
      totalCount: number;
      results: BenchFirmwareSuiteResult[];
    }>("/api/hub/v1/firmware-testing/run-all", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ connectorId }),
    });
  }

  async fetchCommandRegistry(): Promise<BenchCommandRegistryEntry[]> {
    const payload = await this.json<{ commands: BenchCommandRegistryEntry[] }>("/api/hub/v1/command-docs/registry");
    return payload.commands ?? [];
  }

  async executeCommandDoc(input: {
    connectorId: string;
    commandKey: string;
    args?: Record<string, unknown>;
  }): Promise<BenchCommandResult> {
    return this.json<BenchCommandResult>("/api/hub/v1/command-docs/execute", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
  }
}
